import { useState, useEffect, Fragment } from "react";
import { IonGrid, IonRow, IonCol, IonText } from "@ionic/react";
import AccessControlCell from "./AccessControlCell";

import "./GroupPermissionsGrid.css";

// group.helpers.photo_posters on papi: absent resolves to "editors". Kept as
// a bare string rather than joined into the casbin policy - clicking a cell
// in this row sets a MODE, not a per-role toggle, and papi enforces the
// admin/staff floor server-side regardless of what this says.
export type PhotoPostersMode = "admins" | "editors" | "members";

interface GroupPermissionsGridProps {
  // Casbin policy string for the group
  // See https://casbin.org/docs/en/how-it-works for more info
  policy: string;
  onPolicyChange?: (policy: Set<string>) => void;
  // Undefined omits the "Photo Post" row entirely, so this component still
  // works for any caller that only wants the casbin grid.
  photoPosters?: PhotoPostersMode;
  onPhotoPostersChange?: (mode: PhotoPostersMode) => void;
}

// Allowed Group roles
const GROUP_ROLES: string[] = ["admin", "member", "guest", "editor", "none"];

// Allowed resources and their actions
const GROUP_RESOURCE_ACTIONS: Record<string, string[]> = {
  notice: ["edit", "view", "react", "reply"],
  event: ["edit"],
};

// The Default Casbin Group Policy for Groups
// This is used if the permission policy for the group is blank/empty
// See https://casbin.org/docs/en/how-it-works for more info
// May need to store this in Datatown / centrally somewhere,
// so other functions can access the default policy.
export const GROUP_POLICY_DEFAULT: string = `p, admin, notice, edit
p, admin, notice, view
p, admin, notice, react
p, admin, notice, reply
p, admin, event, edit
p, member, notice, view
p, member, notice, react
p, guest, notice, view
p, editor, notice, edit
p, editor, notice, view`;

const newLineExpression = /\r\n|\n\r|\n|\r/g;

// The "Photo Post" row's cell for one column, derived from the group's
// PhotoPosters mode rather than from the casbin Set the rest of this grid
// reads. `nextMode` is what clicking an interactive cell sets it to.
//
// - Admin: always on, never clickable - admins may always post regardless
//   of this setting (the floor papi enforces).
// - Editor: on for "editors" or "members"; toggling off drops to "admins",
//   toggling on rises to "editors".
// - Member: on only for "members"; toggling off drops to "editors" (which
//   leaves Editor on, since "members" already implied it); toggling on
//   rises to "members".
// - Guest, None: not applicable to a group-level posting mode - rendered
//   disabled rather than omitted, so the row lines up with every other row.
function photoPostersCell(
  role: string,
  mode: PhotoPostersMode
): { allowed: boolean; interactive: boolean; nextMode: PhotoPostersMode } {
  switch (role) {
    case "admin":
      return { allowed: true, interactive: false, nextMode: mode };
    case "editor": {
      const allowed = mode === "editors" || mode === "members";
      return {
        allowed,
        interactive: true,
        nextMode: allowed ? "admins" : "editors",
      };
    }
    case "member": {
      const allowed = mode === "members";
      return {
        allowed,
        interactive: true,
        nextMode: allowed ? "editors" : "members",
      };
    }
    default:
      return { allowed: false, interactive: false, nextMode: mode };
  }
}

const GroupPermissionsGrid: React.FC<GroupPermissionsGridProps> = ({
  policy,
  onPolicyChange,
  photoPosters,
  onPhotoPostersChange,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_permissionRows, setPermissionRows] = useState<string[][]>([]);
  const [groupPermissions, setGroupPermissions] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const rows: string[][] = [];

    for (const res of Object.keys(GROUP_RESOURCE_ACTIONS)) {
      for (const act of GROUP_RESOURCE_ACTIONS[res]) {
        const row: string[] = [];
        for (const r of GROUP_ROLES) {
          row.push(`${r}, ${res}, ${act}`);
        }
        rows.push(row);
      }
    }
    setPermissionRows(rows);
  }, []);

  useEffect(() => {
    let parsedPolicy = policy;

    if (!policy || policy === "") {
      parsedPolicy = GROUP_POLICY_DEFAULT;
    }

    parsedPolicy = parsedPolicy.toLowerCase().replace(/ /g, "");

    const policySet = new Set(parsedPolicy.split(newLineExpression));
    setGroupPermissions(policySet);
  }, [policy]);

  const handleTogglePermission = (e: React.MouseEvent, perm: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e && e.preventDefault && e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e && e.stopPropagation && e.stopPropagation();

    const newPolicySet = new Set(groupPermissions);
    if (groupPermissions.has(perm)) {
      // Remove permission
      newPolicySet.delete(perm);
    } else {
      // Add permission
      newPolicySet.add(perm);
    }
    setGroupPermissions(newPolicySet);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onPolicyChange && onPolicyChange(newPolicySet);
  };

  return (
    <Fragment>
      <IonGrid className="group-permission-grid">
        <IonRow>
          <IonCol size="2">&nbsp;</IonCol>
          {GROUP_ROLES.map((r, idx) => (
            <IonCol key={idx} className="group-permission-cell">
              <IonText className="ion-text-capitalize">{r}</IonText>
            </IonCol>
          ))}
        </IonRow>

        {Object.keys(GROUP_RESOURCE_ACTIONS).map((res) => {
          return GROUP_RESOURCE_ACTIONS[res].map(
            (act: string, actIdx: number) => {
              return (
                <IonRow key={actIdx}>
                  <IonCol
                    size="2"
                    className="ion-text-capitalize group-permission-label"
                  >{`${res} ${act}`}</IonCol>
                  {GROUP_ROLES.map((r: string, cellIdx: number) => {
                    return (
                      <IonCol
                        key={cellIdx}
                        className="group-permission-cell"
                        onClick={(e) =>
                          handleTogglePermission(e, `p,${r},${res},${act}`)
                        }
                      >
                        <AccessControlCell
                          role={r}
                          resource={res}
                          action={act}
                          allowed={groupPermissions.has(`p,${r},${res},${act}`)}
                          onClick={(e) =>
                            handleTogglePermission(e, `p,${r},${res},${act}`)
                          }
                        />
                      </IonCol>
                    );
                  })}
                </IonRow>
              );
            }
          );
        })}

        {typeof photoPosters !== "undefined" && (
          <IonRow>
            <IonCol
              size="2"
              className="ion-text-capitalize group-permission-label"
            >
              photo post
            </IonCol>
            {GROUP_ROLES.map((r: string, cellIdx: number) => {
              const cell = photoPostersCell(r, photoPosters);
              const handleClick = (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onPhotoPostersChange?.(cell.nextMode);
              };
              return (
                <IonCol
                  key={cellIdx}
                  className={
                    cell.interactive
                      ? "group-permission-cell"
                      : "group-permission-cell group-permission-cell--static"
                  }
                  onClick={cell.interactive ? handleClick : undefined}
                >
                  <AccessControlCell
                    role={r}
                    resource="photo"
                    action="post"
                    allowed={cell.allowed}
                    interactive={cell.interactive}
                    onClick={cell.interactive ? handleClick : undefined}
                  />
                </IonCol>
              );
            })}
          </IonRow>
        )}
      </IonGrid>

      <p>
        <br />
        <IonText color="secondary">
          Assign group roles (columns) the actions on resources (rows) they are
          allowed to perform with in the group.
        </IonText>
      </p>
      <p>
        <IonText color="secondary">
          Click on a cell to toggle the permission.
        </IonText>
      </p>
    </Fragment>
  );
};

export default GroupPermissionsGrid;
